export interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  href: string;
  /** Single scope or array of scopes (OR logic — any match grants access). */
  scope?: string | string[];
  /** Hide this item when the user already has a broader scope (avoids duplication). */
  excludeIfScope?: string;
  /** Hide this item unless the user's email is on the gencise.ai domain. AND-gated with `scope`. */
  requiresGenciseEmail?: boolean;
  children?: NavigationItem[];
}

export const NAVIGATION_MENU: NavigationItem[] = [
  // Hidden for demo: Dashboard, Market Finder, Quotes
  // {
  //   id: "dashboard",
  //   label: "Dashboard",
  //   icon: "LayoutDashboard",
  //   href: "/home",
  // },
  // {
  //   id: "market-finder",
  //   label: "Market Finder",
  //   icon: "Target",
  //   href: "/market-finder",
  // },
  {
    id: "multi-rater",
    label: "Multi Rater",
    icon: "SquareChartGantt",
    href: "/multi-rater",
    scope: "iq_suite:multi_rater",
  },
  {
    id: "market_finder",
    label: "Broker AI",
    icon: "Send",
    href: "/market-finder",
    // admin:company:market_finder keeps MF admins (and, via parent-grants-
    // child, full admins) in the group even without an iq_suite product
    // scope — their only entry used to be the removed Admin > Market Finder
    // link (GIQS-163).
    scope: ["iq_suite:market_finder", "admin:company:market_finder"],
    children: [
      {
        id: "mf-submissions",
        label: "Submissions",
        icon: "Send",
        href: "/market-finder",
        scope: ["iq_suite:market_finder", "admin:company:market_finder"],
      },
      {
        // GIQS-163: moved out of the Admin section — every Market Finder
        // user gets the queue; the backend scopes rows by tier.
        id: "mf-communication-review",
        label: "Communication Review",
        icon: "MailCheck",
        href: "/market-finder/communication-review",
        scope: ["iq_suite:market_finder", "admin:company:market_finder"],
      },
      {
        // GIQS-164: markets/contacts management moved out of the packet
        // workspace dialogs to a dedicated page.
        id: "mf-markets",
        label: "Markets & Contacts",
        icon: "BookUser",
        href: "/market-finder/markets",
        scope: ["iq_suite:market_finder", "admin:company:market_finder"],
      },
    ],
  },
  {
    id: 'property',
    label: 'Property',
    icon: 'Building',
    href: '/property',
    scope: 'iq_suite:property',
  },
  {
    id: 'bar-tavern',
    label: 'Bar & Tavern',
    icon: 'Beer',
    href: '/bar-tavern',
    scope: 'iq_suite:bar_tavern',
  },
  {
    id: 'transportation',
    label: 'Transportation',
    icon: 'Truck',
    href: '/transportation',
    scope: 'iq_suite:transportation',
  },
  // {
  //   id: "quotes",
  //   label: "Quotes",
  //   icon: "FileText",
  //   href: "/quotes",
  //   scope: "iq_suite:quotes",
  // },
  {
    id: "property_insurance",
    label: "Property Insurance",
    icon: "ShieldCheck",
    href: "/property-insurance",
    scope: "iq_suite:property_insurance",
  },
  {
    id: "admin",
    label: "Admin",
    icon: "Shield",
    href: "/admin",
    scope: ["admin", "admin:company", "admin:email_gateway"],
    children: [
      {
        id: "admin-multi-rater",
        label: "Multi-Rater",
        icon: "Settings",
        href: "/admin/multi-rater",
        scope: "admin:company:multi_rater",
        children: [
          { id: "admin-offices", label: "Offices", icon: "Building2", href: "/admin/multi-rater/offices" },
          { id: "admin-class-codes", label: "Class Codes", icon: "ListOrdered", href: "/admin/multi-rater/class-codes", scope: "admin:company:multi_rater" },
          { id: "admin-commissions", label: "Commissions", icon: "Percent", href: "/admin/multi-rater/commissions", scope: "admin:company:carrier_configs" },
          { id: "admin-carrier-issues", label: "Carrier Issues", icon: "AlertOctagon", href: "/admin/multi-rater/carrier-issues", scope: "admin" },
          { id: "admin-carrier-health", label: "Carrier Health", icon: "HeartPulse", href: "/admin/multi-rater/carrier-health", scope: "admin" },
        ],
      },
      {
        id: "admin-users",
        label: "Users",
        icon: "Users",
        href: "/admin/users",
        scope: "admin:company:users",
      },
      {
        id: "admin-email-gateway",
        label: "Email Gateway",
        icon: "Mail",
        href: "/admin/email-gateway",
        scope: "admin:email_gateway",
        children: [
          { id: "admin-eg-dashboard", label: "Dashboard", icon: "LayoutDashboard", href: "/admin/email-gateway/dashboard" },
          { id: "admin-eg-emails", label: "Emails", icon: "Mail", href: "/admin/email-gateway/emails" },
          { id: "admin-eg-mailboxes", label: "Mailboxes", icon: "Inbox", href: "/admin/email-gateway/mailboxes" },
          { id: "admin-eg-flow-monitor", label: "Flow Monitor", icon: "Activity", href: "/admin/email-gateway/flow-monitor", requiresGenciseEmail: true },
          { id: "admin-eg-lanes", label: "Lanes", icon: "Split", href: "/admin/email-gateway/lanes", requiresGenciseEmail: true },
          { id: "admin-eg-system", label: "System", icon: "Settings", href: "/admin/email-gateway/system" },
        ],
      },
      {
        id: "admin-verticals",
        label: "Vertical Maker",
        icon: "Factory",
        href: "/admin/verticals",
        scope: "admin",
        requiresGenciseEmail: true,
        children: [
          { id: "admin-verticals-runs", label: "Runs", icon: "Factory", href: "/admin/verticals" },
          { id: "admin-verticals-wizard", label: "New wizard", icon: "Plus", href: "/admin/verticals/new" },
          { id: "admin-verticals-discovery", label: "Discovery", icon: "Search", href: "/admin/verticals/discovery" },
        ],
      },
      { id: "admin-companies", label: "Companies", icon: "Building2", href: "/admin/companies", scope: "admin" },
      { id: "admin-scopes", label: "Scopes", icon: "Key", href: "/admin/scopes", scope: "admin" },
      { id: "admin-onboarding", label: "Onboarding", icon: "UserPlus", href: "/admin/onboarding", scope: "admin" },
    ],
  },
  {
    id: "support",
    label: "Support",
    icon: "LifeBuoy",
    // Problem Reports is the support source of truth. Automated analysis is
    // presented inside that workflow instead of competing for navigation.
    href: "/admin/reports",
    scope: ["admin", "iq_suite:support"],
    children: [
      {
        id: "admin-reports",
        label: "Problem Reports",
        icon: "Flag",
        href: "/admin/reports",
        scope: ["admin", "iq_suite:support"],
      },
      {
        // Configures the problem-report system (Jira per vertical, field catalogs).
        // admin-scoped, so support-only users won't see it while admins will.
        id: "admin-report-config",
        label: "Report Config",
        icon: "ClipboardList",
        href: "/admin/report-config",
        scope: "admin",
      },
    ],
  },
];
